import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useCatalogData } from '../hooks/useCatalogData';

const ProductListing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products: catalogProducts, categories: catalogCategories, brands: catalogBrands, loading, error } =
    useCatalogData();
  const products = useMemo(() => catalogProducts ?? [], [catalogProducts]);
  const categories = useMemo(() => catalogCategories ?? [], [catalogCategories]);
  const brandOptions = useMemo(
    () => (catalogBrands ?? []).map((brand) => brand.name).filter(Boolean),
    [catalogBrands]
  );
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(true);
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);

  const maxPrice = useMemo(() => {
    if (!products.length) return 1000;
    const candidate = Math.max(...products.map((product) => product.price || 0), 0);
    return Number.isFinite(candidate) && candidate > 0 ? Math.ceil(candidate) : 1000;
  }, [products]);

  useEffect(() => {
    if (maxPrice > 0 && priceRange[1] === 1000) {
      setPriceRange([0, maxPrice]);
    } else if (maxPrice > 0 && priceRange[1] > maxPrice) {
      setPriceRange(([min]) => [min, maxPrice]);
    }
  }, [maxPrice]);

  useEffect(() => {
    // Get filters from URL
    const category = searchParams.get('category');
    const brand = searchParams.get('brand');
    const search = searchParams.get('search');

    let filtered = [...products];

    // Apply URL filters
    if (category) {
      filtered = filtered.filter(p => p.category === category);
      if (!selectedCategories.includes(category)) {
        setSelectedCategories([category]);
      }
    }
    if (brand) {
      filtered = filtered.filter(p => p.brand === brand);
      if (!selectedBrands.includes(brand)) {
        setSelectedBrands([brand]);
      }
    }
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name.toLowerCase().includes(searchLower) ||
          p.sku.toLowerCase().includes(searchLower) ||
          p.brand.toLowerCase().includes(searchLower)
      );
    }

    // Apply other filters
    if (selectedCategories.length > 0 && !category) {
      filtered = filtered.filter(p => selectedCategories.includes(p.category));
    }
    if (selectedBrands.length > 0 && !brand) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }
    if (priceRange) {
      filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    }
    if (inStockOnly) {
      filtered = filtered.filter(p => p.stock > 0);
    }
    if (minRating > 0) {
      filtered = filtered.filter(p => p.rating >= minRating);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  }, [
    searchParams,
    selectedCategories,
    selectedBrands,
    priceRange,
    inStockOnly,
    minRating,
    sortBy,
    products,
  ]);

  useEffect(() => {
    if (!loading && !products.length) {
      setFilteredProducts([]);
    }
  }, [loading, products]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setPriceRange([0, maxPrice]);
    setInStockOnly(false);
    setMinRating(0);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Industrial Tools & Equipment</h1>
            <p className="text-gray-600 mt-1">{filteredProducts.length} products found</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center space-x-2 px-4 py-2 bg-white border rounded hover:bg-gray-50"
            >
              <Filter size={18} />
              <span>Filters</span>
            </button>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border rounded focus:outline-none focus:border-red-500"
            >
              <option value="default">Sort By: Default</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
              <option value="rating">Highest Rated</option>
            </select>
            
            <div className="hidden md:flex space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 border rounded ${viewMode === 'grid' ? 'bg-red-600 text-white' : 'bg-white'}`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 border rounded ${viewMode === 'list' ? 'bg-red-600 text-white' : 'bg-white'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-bold text-lg">Filters</h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Clear All
                </button>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <label key={category.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                        className="mr-2 accent-red-600"
                      />
                      <span className="text-sm">{category.name}</span>
                    </label>
                  ))}
                  {categories.length === 0 && (
                    <div className="text-sm text-gray-500">
                      {error ? 'Unable to load categories.' : 'No categories available.'}
                    </div>
                  )}
                </div>
              </div>

              {/* Brands */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Brands</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {brandOptions.map((brand) => (
                    <label key={brand} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        className="mr-2 accent-red-600"
                      />
                      <span className="text-sm">{brand}</span>
                    </label>
                  ))}
                  {brandOptions.length === 0 && (
                    <div className="text-sm text-gray-500">
                      {error ? 'Unable to load brands.' : 'No brands available.'}
                    </div>
                  )}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Price Range</h3>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full accent-red-600"
                  />
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Minimum Rating</h3>
                <div className="space-y-2">
                  {[4, 3, 2, 1].map((rating) => (
                    <label key={rating} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="rating"
                        checked={minRating === rating}
                        onChange={() => setMinRating(rating)}
                        className="mr-2 accent-red-600"
                      />
                      <div className="flex items-center">
                        {[...Array(rating)].map((_, i) => (
                          <span key={i} className="text-yellow-400">★</span>
                        ))}
                        <span className="text-sm text-gray-600 ml-1">& Up</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Stock Status */}
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="mr-2 accent-red-600"
                  />
                  <span className="text-sm font-semibold">In Stock Only</span>
                </label>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {filteredProducts.length > 0 ? (
              <div
                className={`grid gap-6 ${
                  viewMode === 'grid'
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    : 'grid-cols-1'
                }`}
              >
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : loading ? (
              <div className="text-center py-12 text-gray-500">Loading products...</div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 text-red-600 hover:text-red-700 font-semibold"
                >
                  Clear all filters
                </button>
                {error && (
                  <p className="mt-4 text-sm text-gray-500">
                    There was a problem loading products. Showing available data.
                  </p>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
